import React, { FC, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Contract,
  ContractTenant,
  UserMetadata,
} from '../../../common/validations'
import { TerminationDisallowedReason } from '../../../common/types'
import { ContractModificationErrorHandler } from '../ContractChanges/ContractModificationErrorHandler'
import { ContractChangeTypeChoice } from '../ContractChanges/components/ContractChangeTypeChoice/ContractChangeTypeChoice'
import { hasActiveTermination } from '../ContractChanges/utils/hasActiveTermination'
import { ContractChangesViewState } from '../ContractChanges/types'
import { AddTenantForm } from './AddTenant/AddTenantForm'
import { MoveOutForm } from './MoveOutForm/MoveOutForm'

import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

type ResidentChange = 'moveIn' | 'moveOut'

const ADD_TENANT_DISALLOWED_REASONS: TerminationDisallowedReason[] = [
  TerminationDisallowedReason.HAS_GUARDIAN_OF_INTERESTS,
  TerminationDisallowedReason.PENDING_MODIFICATION_REQUEST,
  TerminationDisallowedReason.PENDING_TERMINATION_TENANT_APPROVALS,
  TerminationDisallowedReason.PENDING_TERMINATION_REVIEW,
  TerminationDisallowedReason.CONTRACT_TERMINATED,
  TerminationDisallowedReason.NOT_CONTRACT_PARTY,
  TerminationDisallowedReason.OPEN_CONTINUATION_OFFER,
  TerminationDisallowedReason.PENDING_CONTRACT_RESIGN,
]

type ResidentChangesFormProps = {
  view: ContractChangesViewState
  setView: (view: ContractChangesViewState) => void
  contract: Contract
  tenants: ContractTenant[]
  userMetadata: UserMetadata
}

export const ResidentChangesForm: FC<ResidentChangesFormProps> = ({
  view,
  setView,
  contract,
  tenants,
  userMetadata,
}) => {
  const { t } = useTranslation(['contractModification'])
  const { residentChangesPath } = useContractPaths()
  const [params] = useSearchParams()
  const returnedFromConfirmation = params.has('success') || params.has('error')
  const [residentChange, setResidentChange] = useState<
    ResidentChange | undefined
  >(returnedFromConfirmation ? 'moveOut' : undefined)

  const reason = contract.terminationDisallowedReason
  const addTenantAllowed =
    (reason === undefined || !ADD_TENANT_DISALLOWED_REASONS.includes(reason)) &&
    !hasActiveTermination(contract, tenants)

  const resetResidentChange = () => setResidentChange(undefined)

  return (
    <>
      {view === 'form' && !params.has('error') && (
        <ContractChangeTypeChoice<ResidentChange>
          heading={t('contractModification:headings.residentChangeType')}
          options={[
            {
              id: 'moveIn',
              label: t('contractModification:residentChanges.moveIn'),
              description: t(
                'contractModification:residentChanges.moveInDescription'
              ),
              disabled: !addTenantAllowed,
            },
            {
              id: 'moveOut',
              label: t('contractModification:residentChanges.moveOut'),
              description: t(
                'contractModification:residentChanges.moveOutDescription'
              ),
              disabled: false,
            },
          ]}
          value={residentChange}
          onChange={setResidentChange}
        />
      )}

      {residentChange === 'moveIn' && (
        <ContractModificationErrorHandler
          view="form"
          restartPath={residentChangesPath}
        >
          <AddTenantForm
            view={view}
            setView={setView}
            contract={contract}
            userMetadata={userMetadata}
            onCancel={resetResidentChange}
          />
        </ContractModificationErrorHandler>
      )}

      {residentChange === 'moveOut' && (
        <ContractModificationErrorHandler
          view="form"
          restartPath={residentChangesPath}
        >
          <MoveOutForm
            view={view}
            setView={setView}
            contract={contract}
            tenants={tenants}
            userMetadata={userMetadata}
            onCancel={resetResidentChange}
          />
        </ContractModificationErrorHandler>
      )}
    </>
  )
}
